import type {
  CareerSuggestion, IncorporationLine, IncorporationRow, NotesSuggestion, ReleasedRequirement,
  RequirementSummary,
} from '@/api/erp-integration';
import type { RequirementFrequency } from '@/api/planning/requirements';
import { formatDate } from './erp-format';

// What a coordinator decides for one Service line on the incorporation screen
// (ADR 0001, "The incorporation screen" and "Package lines"). Each row is one
// Requirement the line feeds: an open line becomes a new Requirement or
// continues one the patient has, a Package line one per career. A line already
// in Requirements keeps them, updates them (a Renewal whose quantity or notes
// changed), moves (one career) or drops a career (a Package line). A line may
// be dismissed for good instead; one of an unmapped product must be.
export type RowMode = 'create' | 'continue' | 'keep' | 'update';

export interface RequirementValues {
  career: string | null;
  skillId: number | null;
  durationMin: number | null;
  frequency: RequirementFrequency;
  visitsPerPeriod: number | null;
}

export interface RowDraft extends RequirementValues {
  key: number;
  mode: RowMode;
  // The Requirement the line already feeds through this row, if any, and its
  // values as they are (a row set back to "keep" shows them again).
  ownRequirementId: number | null;
  original: RequirementValues | null;
  // The Requirement it continues or moves to.
  requirementId: number | null;
  // What the AI read in the notes for this row's career (a Package line), with
  // its quote, days and times.
  reading: NotesSuggestion | null;
}

export interface LineDraft {
  lineId: number;
  isPackage: boolean;
  unmapped: boolean;
  dismissed: boolean;
  // The Requirements the line fed when the screen opened.
  ownIds: number[];
  rows: RowDraft[];
}

export const MAX_VISITS_PER_PERIOD = 100;
export const MAX_DURATION_MIN = 1440;
const MINUTES_PER_HOUR = 60;

let lastKey = 0;

function nextKey(): number {
  lastKey += 1;

  return lastKey;
}

// The first value present, in order of preference.
function first<T>(...values: (T | null | undefined)[]): T | null {
  return values.find((value) => value !== null && value !== undefined) ?? null;
}

const NO_RULE = { career: null, durationMin: null };
const NO_SUGGESTION = { frequency: null, visitsPerPeriod: null };

// A new row starts from the AI reading of the notes, then the product map and
// the quantity sold. A Package line's row starts empty: those readings are of
// the whole line, not of one career. Nothing is saved until the coordinator
// confirms.
export function suggestedValues(line: IncorporationLine): RequirementValues {
  const rule = line.rule ?? NO_RULE;
  if (line.kind === 'package') {
    return { career: null, skillId: null, durationMin: rule.durationMin, frequency: 'weekly',
      visitsPerPeriod: null };
  }

  const ai = line.ai ?? {};
  const suggestion = line.suggestion ?? NO_SUGGESTION;

  return {
    career: rule.career,
    skillId: null,
    durationMin: first(ai.durationMin, rule.durationMin),
    frequency: first<RequirementFrequency>(ai.frequency, suggestion.frequency) ?? 'weekly',
    visitsPerPeriod: first(ai.visitsPerPeriod, suggestion.visitsPerPeriod),
  };
}

function currentValues(own: RequirementSummary): RequirementValues {
  const { career, skillId, durationMin, frequency, visitsPerPeriod } = own;

  return { career, skillId, durationMin, frequency, visitsPerPeriod };
}

function keptRow(own: RequirementSummary): RowDraft {
  const values = currentValues(own);

  return { key: nextKey(), mode: 'keep', ownRequirementId: own.id, original: values,
    requirementId: null, reading: null, ...values };
}

export function newRow(line: IncorporationLine): RowDraft {
  return { key: nextKey(), mode: 'create', ownRequirementId: null, original: null,
    requirementId: null, reading: null, ...suggestedValues(line) };
}

// A Package line's career as the AI read it in the notes.
function suggestedRow(line: IncorporationLine, reading: CareerSuggestion): RowDraft {
  return {
    ...newRow(line),
    career: reading.career,
    durationMin: first(reading.durationMin, line.rule?.durationMin),
    frequency: reading.frequency ?? 'weekly',
    visitsPerPeriod: reading.visitsPerPeriod ?? null,
    reading,
  };
}

// An open line starts from what was read: a Package line with one row per
// career the AI found in the notes (one empty row when none), any other line
// with one row.
function openRows(line: IncorporationLine): RowDraft[] {
  if (line.kind === 'unmapped') return [];
  if (line.kind === 'package' && line.aiCareers.length) {
    return line.aiCareers.map((reading) => suggestedRow(line, reading));
  }

  return [newRow(line)];
}

export function initialDraft(line: IncorporationLine): LineDraft {
  const own = line.requirements;
  const rows = own.length ? own.map(keptRow) : openRows(line);

  return { lineId: line.id, isPackage: line.kind === 'package', unmapped: line.kind === 'unmapped',
    dismissed: false, ownIds: own.map((requirement) => requirement.id), rows };
}

function inRange(value: number | null, max: number): boolean {
  return value !== null && Number.isInteger(value) && value >= 1 && value <= max;
}

export interface RowErrors {
  requirement?: string;
  career?: string;
  visits?: string;
  duration?: string;
}

function valuesErrors(row: RowDraft): RowErrors {
  const errors: RowErrors = {};
  if (!row.career) errors.career = 'Elige la especialidad.';
  if (!inRange(row.visitsPerPeriod, MAX_VISITS_PER_PERIOD)) {
    errors.visits = `Indica las visitas por período (1 a ${MAX_VISITS_PER_PERIOD}).`;
  }
  if (!inRange(row.durationMin, MAX_DURATION_MIN)) {
    errors.duration = `Indica la duración (hasta ${MAX_DURATION_MIN / MINUTES_PER_HOUR} horas).`;
  }

  return errors;
}

// What each field of a row is missing, to show next to it.
export function rowErrors(row: RowDraft): RowErrors {
  if (row.mode === 'keep') return {};
  if (row.mode === 'continue') {
    return row.requirementId ? {} : { requirement: 'Elige el requerimiento.' };
  }

  return valuesErrors(row);
}

// What one row is missing first, or null.
export function rowError(row: RowDraft): string | null {
  const errors = rowErrors(row);

  return errors.requirement ?? errors.career ?? errors.visits ?? errors.duration ?? null;
}

// The Requirement each row ends in, when it is an existing one.
function targets(draft: LineDraft): number[] {
  return draft.rows.map((row) => (row.mode === 'continue' ? row.requirementId : row.ownRequirementId))
    .filter((id): id is number => id !== null);
}

// A line of an unmapped product can only be dismissed, or left as it is when
// placed.
function unmappedError(draft: LineDraft): string | null {
  if (!draft.unmapped) return null;
  if (!draft.ownIds.length) return 'Descarta la línea, o cancela y pide a Kuido mapear el producto.';

  return draft.rows.some((row) => row.mode !== 'keep') ?
    'El producto ya no está mapeado: deja la línea como está o pide a Kuido mapearlo.' : null;
}

function emptyError(draft: LineDraft): string | null {
  if (draft.rows.length) return null;

  return draft.ownIds.length ? 'Deja al menos una especialidad en la línea.' :
    'Agrega al menos una especialidad, o descarta la línea.';
}

function duplicateError(draft: LineDraft): string | null {
  const ids = targets(draft);

  return new Set(ids).size === ids.length ? null : 'Un requerimiento quedó dos veces en la línea.';
}

// What the line itself is missing (not one of its rows), or null.
export function lineError(draft: LineDraft): string | null {
  if (draft.dismissed) return null;

  return unmappedError(draft) ?? emptyError(draft) ?? duplicateError(draft);
}

// What is missing for the line to be sent, or null.
export function draftError(draft: LineDraft): string | null {
  if (draft.dismissed) return null;

  return lineError(draft) ?? draft.rows.map(rowError).find((error) => error !== null) ?? null;
}

function requirementValues(row: RowDraft) {
  return {
    career: row.career as string,
    skillId: row.skillId,
    durationMin: row.durationMin,
    frequency: row.frequency,
    visitsPerPeriod: row.visitsPerPeriod as number,
  };
}

function toRow(lineId: number, row: RowDraft): IncorporationRow {
  const lineIds = [lineId];
  if (row.mode === 'continue') return { lineIds, requirementId: row.requirementId as number };
  if (row.mode === 'keep') return { lineIds, requirementId: row.ownRequirementId as number };
  if (row.mode === 'update') {
    return { lineIds, requirementId: row.ownRequirementId as number, ...requirementValues(row) };
  }

  return { lineIds, ...requirementValues(row) };
}

// The rows Erp::Incorporation::Confirm takes for the line: one per Requirement
// to create, continue or update. A Requirement kept as it was needs none.
export function toRows(draft: LineDraft): IncorporationRow[] {
  if (draft.dismissed) return [];

  return draft.rows.filter((row) => row.mode !== 'keep').map((row) => toRow(draft.lineId, row));
}

// The Requirements a Package line stops feeding: those no row ends in (a
// career taken off, or moved to another Requirement). A line of one career
// moves through its row instead.
export function toReleased(draft: LineDraft): ReleasedRequirement[] {
  if (draft.dismissed || !draft.isPackage) return [];
  const kept = targets(draft);

  return draft.ownIds.filter((id) => !kept.includes(id))
    .map((requirementId) => ({ lineId: draft.lineId, requirementId }));
}

export function requirementTitle(requirement: RequirementSummary, careerName: string): string {
  return `${careerName} · ${requirement.visitsPerPeriod} visitas (desde ` +
    `${formatDate(requirement.startsOn)})`;
}
